import React, { useState, useEffect, useRef } from "react";
import { EmailTemplate } from "../types";

interface TemplateSelectorProps {
  selectedTemplateId: string | null; // null = "None"
  onSelectTemplate: (template: EmailTemplate | null) => void;
  accessToken: string;
  onAddCustomTemplate: () => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplateId,
  onSelectTemplate,
  accessToken,
  onAddCustomTemplate,
}) => {
  const [templates, setTemplates] = useState<{
    default: EmailTemplate[];
    custom: EmailTemplate[];
  }>({ default: [], custom: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch("/api/templates", {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch templates");
        }

        const data = await response.json();
        setTemplates(data);
      } catch (err) {
        console.error("Error fetching templates:", err);
        setError("Failed to load templates");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, [accessToken]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Handle template deletion
  const handleDeleteTemplate = async (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    if (!confirm("Are you sure you want to delete this template?")) return;

    try {
      const response = await fetch(`/api/templates/${templateId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete template");
      }

      // Remove from local state
      setTemplates((prev) => ({
        ...prev,
        custom: prev.custom.filter((t) => t.template_id !== templateId),
      }));

      // If the deleted template was selected, reset to "None"
      if (selectedTemplateId === templateId) {
        onSelectTemplate(null);
      }
    } catch (err) {
      console.error("Error deleting template:", err);
      alert("Failed to delete template");
    }
  };

  // Get the selected template
  const getSelectedTemplate = (): EmailTemplate | null => {
    if (!selectedTemplateId) return null;
    return (
      templates.default.find((t) => t.template_id === selectedTemplateId) ||
      templates.custom.find((t) => t.template_id === selectedTemplateId) ||
      null
    );
  };

  const selectedTemplate = getSelectedTemplate();

  // Get template icon color based on template ID
  const getTemplateColor = (templateId: string | null) => {
    if (!templateId) return "bg-text-muted";
    switch (templateId) {
      case "friendly":
        return "bg-teal-500";
      case "minimal":
        return "bg-text-secondary";
      case "newsletter":
        return "bg-amber-500";
      default:
        return "bg-rose-accent";
    }
  };

  if (isLoading) {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-text-primary mb-2">Template (optional)</label>
        <div className="flex items-center gap-2 h-10 px-3 rounded-sm bg-navy-mid/50 border border-border-strong">
          <div className="w-4 h-4 border-2 border-text-muted border-t-text-primary rounded-full animate-spin" />
          <span className="text-sm text-text-secondary">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-text-primary mb-2">Template (optional)</label>
        <div className="flex items-center gap-2 h-10 px-3 rounded-sm bg-navy-mid/50 border border-border-strong text-amber-400">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4" ref={dropdownRef}>
      <label className="block text-sm font-medium text-text-primary mb-2">Template (optional)</label>

      {/* Dropdown trigger */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center justify-between gap-2 h-10 px-3 rounded-sm bg-navy-mid/50 border transition-colors ${
            isOpen ? "border-rose-accent ring-1 ring-rose-accent/30" : "border-border-strong hover:border-border-strong"
          }`}
        >
          <div className="flex items-center gap-2.5">
            <div className={`w-3 h-3 rounded-sm ${getTemplateColor(selectedTemplateId)}`} />
            <span className="text-sm text-text-primary">
              {selectedTemplate ? selectedTemplate.name : "None"}
            </span>
            {selectedTemplate?.type === "custom" && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-rose-dim text-rose-accent">Custom</span>
            )}
          </div>
          <svg
            className={`w-4 h-4 text-text-secondary transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 py-1 rounded-sm bg-navy border border-border-strong shadow-xl max-h-64 overflow-y-auto">
            {/* None option */}
            <button
              type="button"
              onClick={() => {
                onSelectTemplate(null);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-navy-mid/50 transition-colors ${
                selectedTemplateId === null ? "bg-navy-mid/30" : ""
              }`}
            >
              <div className="w-3 h-3 rounded-sm bg-text-muted" />
              <span className="text-sm text-text-primary">None</span>
              {selectedTemplateId === null && (
                <svg className="w-4 h-4 text-rose-accent ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>

            {/* Default templates */}
            {templates.default.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-xs font-medium text-text-muted uppercase tracking-wider">
                  Templates
                </div>
                {templates.default.map((template) => (
                  <button
                    key={template.template_id}
                    type="button"
                    onClick={() => {
                      onSelectTemplate(template);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-navy-mid/50 transition-colors ${
                      selectedTemplateId === template.template_id ? "bg-navy-mid/30" : ""
                    }`}
                  >
                    <div className={`w-3 h-3 rounded-sm ${getTemplateColor(template.template_id)}`} />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-text-primary">{template.name}</span>
                      <p className="text-xs text-text-muted truncate">{template.description}</p>
                    </div>
                    {selectedTemplateId === template.template_id && (
                      <svg className="w-4 h-4 text-rose-accent flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
              </>
            )}

            {/* Custom templates */}
            {templates.custom.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-xs font-medium text-text-muted uppercase tracking-wider border-t border-border-strong mt-1 pt-2">
                  Custom Templates
                </div>
                {templates.custom.map((template) => (
                  <div
                    key={template.template_id}
                    className={`group flex items-center gap-2.5 px-3 py-2 hover:bg-navy-mid/50 transition-colors ${
                      selectedTemplateId === template.template_id ? "bg-navy-mid/30" : ""
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onSelectTemplate(template);
                        setIsOpen(false);
                      }}
                      className="flex-1 flex items-center gap-2.5 text-left min-w-0"
                    >
                      <div className={`w-3 h-3 rounded-sm ${getTemplateColor(template.template_id)}`} />
                      <span className="text-sm text-text-primary truncate">{template.name}</span>
                      {selectedTemplateId === template.template_id && (
                        <svg className="w-4 h-4 text-rose-accent flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteTemplate(e, template.template_id)}
                      className="p-1 rounded text-text-muted hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                      title="Delete template"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </>
            )}

            {/* Add custom template */}
            <div className="border-t border-border-strong mt-1 pt-1">
              <button
                type="button"
                onClick={() => {
                  onAddCustomTemplate();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-navy-mid/50 transition-colors"
              >
                <div className="w-3 h-3 rounded-sm bg-text-muted/50 flex items-center justify-center">
                  <svg className="w-2 h-2 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <span className="text-sm text-text-secondary">Add custom template...</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateSelector;
